import param from 'jquery-param';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { action, get, setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { instrumentRoutePerformance } from '@sentry/ember';

import type Transition from '@ember/routing/transition';
import type { TError } from 'volta/utils/error-utils';
import type { IntlService } from 'ember-intl';
import type SessionService from 'ember-simple-auth/services/session';
import { DefaultResourceName, type IResourceName } from 'volta/models/base-model';
import type AppNavState from 'volta/services/app-nav-state';
import type AuthorizationService from 'volta/services/authorization';
import type BvFlashService from 'volta/services/bv-flash';
import type LocalPreferencesService from 'volta/services/local-preferences';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type ProtectedController from 'volta/controllers/protected-controller';
import type RouterService from '@ember/routing/router-service';
import type LabelService from 'volta/services/label-service';
import type AppSettings from 'volta/services/app-settings';

export const NAVIGATION_HISTORY_KEY = 'ROUTING_HISTORY';

export interface IHistoryRecord {
  id: string;
  key: string;
  title: string;
  tab?: string;
  v?: string;
  url: string;
  visitCount: number;
  lastVisitedAt: Date;
}

export class BaseRoute extends Route {
  // Services
  // ~~~~~
  @service session!: SessionService;
  @service intl!: IntlService;
  @service labelService!: LabelService;

  @service bvFlash!: BvFlashService;
  @service sessionUser!: SessionUserService;
  @service authorization!: AuthorizationService;
  @service localPreferences!: LocalPreferencesService;
  @service appNavState!: AppNavState;
  @service store!: StoreService;
  @service router!: RouterService;
  @service appSettings!: AppSettings;
  // Properties
  // ~~~~~

  @tracked queryParamsValues: TQueryParams = {};
  @tracked documentTitle!: string[];

  resourceName: IResourceName = DefaultResourceName;

  // Lifecycle Hooks
  // ~~~~~

  async beforeModel(transition: Transition) {
    try {
      const user = await this.sessionUser.reloadUser();
      this.checkAccessRight(transition, this.resourceName.plural ?? this.resourceName.singular);
      return user;
    } catch (error) {
      return this._onDefaultError(error as TError);
    }
  }

  model(params: any, _transition: Transition) {
    this.queryParamsValues = params;
  }

  setupController(
    controller: ProtectedController,
    model: NonNullable<unknown>,
    transition: Transition
  ) {
    super.setupController(controller, model, transition);

    setProperties(controller, {
      resourceName: this.resourceName,
      route: this
    });
  }

  afterModel(model: NonNullable<unknown>, transition: Transition) {
    transition.then(() => {
      this.setDocumentTitle(model, transition, this);
      this.createHistory(transition);
    });
  }

  setDocumentTitle(_model: NonNullable<unknown>, _transition: Transition, _route: Route) {
    // empty
  }

  // Actions
  // ~~~~~

  @action
  refreshRoute() {
    this.refresh();
  }

  @action
  refreshModel(params?: any) {
    this.router.transitionTo({ queryParams: params ?? this.queryParamsValues });
  }

  // Private Callbacks
  // ~~~~~

  protected _onDefaultError(error: TError) {
    defaultErrorHandler(error as TError);
    this.bvFlash.error(
      this.intl.t('errors.route', {
        route: this.routeName ?? this.intl.t(DefaultResourceName.singular)
      })
    );
  }

  /**
   * Checks if the user has access rights to view the requested route.
   * If the user is a supplier and the requested route is not in the list of authorized routes,
   * redirects to the PO workbench.
   * If the user is not a supplier and does not have access rights to view the requested route,
   * redirects to the protected index.
   */
  protected checkAccessRight(transition: Transition, canView: string, ...rest: string[]) {
    const supplierPortalAuthRoutes = [
      'protected.account',
      'protected.po-workbench',
      'protected.po-workbench.line',
      'protected.po-workbench.line.index',
      'protected.po-workbench.line.tasks',
      'protected.po-workbench.line.activities',
      'protected.reports',
      'protected.report'
    ];
    const operatorWorkbenchAuthRoutes = [
      'protected.account',
      'protected.operator-workbench',
      'protected.reports',
      'protected.report'
    ];
    const operatorWorkbenchDisabled = !this.appSettings.features?.experimental.operatorWorkbench;
    const user = this.sessionUser.user;
    const unauthorizedEntities = this.authorization.cannotViewAnyList(canView, ...rest);
    if (
      user?.isSupplier &&
      !supplierPortalAuthRoutes.some(
        (r) => r === transition.to?.name || r === transition.to?.parent?.name
      )
    ) {
      this.router.transitionTo('protected.po-workbench');
    } else if (
      user?.isOperator &&
      !operatorWorkbenchDisabled &&
      !operatorWorkbenchAuthRoutes.some(
        (r) => r === transition.to?.name || r === transition.to?.parent?.name
      )
    ) {
      this.router.transitionTo('protected.operator-workbench');
    } else if (!user?.isExternalUser && canView && unauthorizedEntities.length > 0) {
      // Do not pass by ProtectedRoute.beforeModel at this point since it's already been initialized.
      this.handleUnauthorizedRoute();
      defaultErrorHandler(
        `Missing Permissions: ${unauthorizedEntities
          .map((e) => this.labelService.tOrElse(e))
          .join(', ')} `
      );
    }
  }

  protected handleUnauthorizedRoute() {
    this.router.transitionTo(this.appNavState.homeLink);
    this.bvFlash.error(this.intl.t('errors.permissions.route.unauthorized'));
  }

  protected generateCreationTitle() {
    this.documentTitle = [
      this.intl.t('bvList.create', {
        resourceNameSingular: this.intl.t(this.resourceName.singular).toLowerCase()
      })
    ];
  }

  protected generateItemTitle(model: any, key: string = 'code') {
    this.documentTitle = [
      this.intl.t('bvList.singleTitle', {
        resourceNameSingular: this.intl.t(this.resourceName.singular),
        code: get(model, key)
      })
    ];
  }

  private concatTitleParams(arr: string[] = [], separator: string) {
    let title = '';
    for (const w of arr) {
      const translated = this.intl.exists(w) ? this.intl.t(w) : w;
      title = title ? title.concat(separator, translated) : translated;
    }
    return title.trim();
  }

  private createHistory(transition: Transition) {
    const { documentTitle } = this;

    if (!documentTitle || documentTitle.length === 0) {
      return;
    }

    const history: { [key: string]: IHistoryRecord } =
      this.localPreferences.getItem(NAVIGATION_HISTORY_KEY) ?? {};

    const { name, queryParams } = transition.to ?? {};
    const url = new URL(window.location.href);
    const fullPath = url.pathname + url.search;
    const { tab, v } = queryParams ?? {};
    const keyQp: TQueryParams = {};

    if (tab) {
      keyQp['tab'] = tab as string;
    }

    if (v) {
      keyQp['v'] = v as string;
    }

    const historyRecordKey =
      url.pathname + (Object.keys(keyQp).length > 0 ? `?${param(keyQp)}` : '');

    const navName = Object.values(this.appNavState.searchMenuContent).find(
      (nav) => nav.route === name
    );

    const title = navName?.label ?? this.concatTitleParams(documentTitle, ' - ');

    const historyRecord: IHistoryRecord = {
      id: historyRecordKey,
      key: historyRecordKey,
      title,
      tab: tab as string,
      v: v as string,
      url: fullPath,
      visitCount: 1,
      lastVisitedAt: new Date()
    };

    if (history[historyRecordKey]) {
      historyRecord.visitCount = history[historyRecordKey]!.visitCount + 1;
    }

    history[historyRecordKey] = historyRecord;

    this.localPreferences.setItem(NAVIGATION_HISTORY_KEY, history);
    this.appNavState.fillHistory(history);
  }
}

// For Sentry
export default instrumentRoutePerformance(BaseRoute) as unknown as typeof BaseRoute;
